// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import {
  type AgentCoreConfig,
  type AgentPattern,
  type ChunkParser,
  type StreamCallback,
} from "./types"
import { parseStrandsChunk } from "./parsers/strands"
import { parseLanggraphChunk } from "./parsers/langgraph"
import { parseClaudeAgentSdkChunk } from "./parsers/claude-agent-sdk"
import { parseAguiChunk } from "./parsers/agui"
import { readSSEStream } from "./utils/sse"

/** Resolve parser from pattern prefix. Defaults to strands parser. */
function getParser(pattern: AgentPattern): ChunkParser {
  if (pattern.startsWith("agui-")) return parseAguiChunk
  if (pattern.startsWith("langgraph-")) return parseLanggraphChunk
  if (pattern.startsWith("claude-")) return parseClaudeAgentSdkChunk
  if (pattern.startsWith("strands-")) return parseStrandsChunk
  return parseStrandsChunk
}

export class AgentCoreClient {
  private runtimeArn: string
  private region: string
  private pattern: AgentPattern
  private parser: ChunkParser
  private localAgentUrl?: string

  constructor(config: AgentCoreConfig) {
    this.runtimeArn = config.runtimeArn
    this.region = config.region ?? "ap-southeast-2"
    this.pattern = config.pattern
    this.parser = getParser(config.pattern)
    this.localAgentUrl = config.localAgentUrl
  }

  generateSessionId(): string {
    return crypto.randomUUID()
  }

  /**
   * Build the URL used to reach the agent runtime.
   *
   * In local development mode (Docker Compose) this is the direct agent URL;
   * otherwise it is the deployed AgentCore Runtime invocations endpoint.
   *
   * @returns The fully-qualified invocations URL.
   */
  private buildInvocationUrl(): string {
    if (this.localAgentUrl) {
      // Local development mode - call agent directly
      return this.localAgentUrl
    }
    // Production mode - call AgentCore API
    const endpoint = `https://bedrock-agentcore.${this.region}.amazonaws.com`
    const escapedArn = encodeURIComponent(this.runtimeArn)
    return `${endpoint}/runtimes/${escapedArn}/invocations?qualifier=DEFAULT`
  }

  /**
   * Returns true when the client is configured to use a local agent URL
   * (Docker Compose mode) rather than the deployed AgentCore Runtime.
   */
  isLocalMode(): boolean {
    return !!this.localAgentUrl
  }

  /**
   * Invoke the assistant's own runtime and stream the response.
   *
   * Dispatch is by endpoint (the resolved local URL / runtime ARN), so no
   * assistant discriminator is sent in the request body.
   *
   * @param query       - The user prompt to send to the runtime.
   * @param sessionId   - The runtime session UUID for this conversation.
   * @param accessToken - Auth0 access token (required in deployed modes).
   * @param onEvent     - SSE stream callback invoked for each parsed event.
   * @param signal      - Optional AbortSignal. When aborted, the underlying
   *                      fetch and SSE stream are cancelled so no further
   *                      events are emitted.
   * @param extraBody   - Optional agent-specific, request-scoped fields to
   *                      merge into the request body (strands pattern only).
   *                      Forwarded opaquely: the client does not interpret the
   *                      keys. Used to carry things like a collection scope
   *                      without the generic client knowing about them.
   * @returns A promise that resolves when the stream has been fully consumed
   *          or the request has been aborted.
   */
  async invoke(
    query: string,
    sessionId: string,
    accessToken: string,
    onEvent: StreamCallback,
    signal?: AbortSignal,
    extraBody?: Record<string, unknown>
  ): Promise<void> {
    if (!this.localAgentUrl && !accessToken) throw new Error("No valid access token found.")
    if (!this.localAgentUrl && !this.runtimeArn) throw new Error("Agent Runtime ARN not configured.")

    const url = this.buildInvocationUrl()

    const traceId = `1-${Math.floor(Date.now() / 1000).toString(16)}-${crypto.randomUUID()}`

    // Build payload based on pattern — AG-UI protocol expects a different format
    const body = this.pattern.startsWith("agui-")
      ? {
          threadId: sessionId,
          runId: crypto.randomUUID(),
          messages: [{ id: crypto.randomUUID(), role: "user", content: query }],
          state: {},
          tools: [],
          context: [],
          forwardedProps: {},
        }
      : {
          prompt: query,
          runtimeSessionId: sessionId,
          // IANA timezone (e.g. "Australia/Perth") so the runtime can bucket
          // daily token usage by the user's LOCAL date rather than UTC. The
          // server derives the date from its own clock using this zone.
          clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // Agent-specific request-scoped fields (e.g. a collection scope),
          // merged opaquely. The client does not interpret these keys.
          ...(extraBody ?? {}),
        }

    // User identity is extracted server-side from the validated JWT token
    // (Authorization header), not sent in the payload body. This prevents
    // impersonation via prompt injection.
    const response = await fetch(url, {
      method: "POST",
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        "X-Amzn-Trace-Id": traceId,
        "Content-Type": "application/json",
        "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
      },
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP ${response.status}: ${errorText}`)
    }

    await readSSEStream(response, this.parser, onEvent, signal)
  }

  /**
   * Fetch an agent's system prompt from the runtime.
   *
   * Sends an ``action: "get_system_prompt"`` payload to the same invocations
   * endpoint the agent uses. The backend registry is the single source of
   * truth for prompt text, so the frontend never duplicates it. Works in both
   * local Docker and deployed modes since both route through this endpoint.
   * Dispatch is by endpoint, so no assistant discriminator is sent in the body.
   *
   * @param accessToken - Auth0 access token (required in deployed mode).
   * @returns The runtime's full system prompt string.
   * @throws If the request fails or the response contains no prompt.
   */
  async getSystemPrompt(accessToken: string): Promise<string> {
    if (!this.localAgentUrl && !accessToken) throw new Error("No valid access token found.")
    if (!this.localAgentUrl && !this.runtimeArn) throw new Error("Agent Runtime ARN not configured.")

    const url = this.buildInvocationUrl()
    const sessionId = crypto.randomUUID()

    const response = await fetch(url, {
      method: "POST",
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        "Content-Type": "application/json",
        "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
      },
      body: JSON.stringify({ action: "get_system_prompt" }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP ${response.status}: ${errorText}`)
    }

    // The entrypoint yields a single { type: "system_prompt", prompt } event,
    // streamed as an SSE "data: {...}" line. Scan the stream for it.
    let prompt: string | null = null
    await readSSEStream(response, (line, callback) => {
      if (!line.startsWith("data: ")) return
      const data = line.substring(6).trim()
      if (!data) return
      try {
        const json = JSON.parse(data)
        if (json.type === "system_prompt" && typeof json.prompt === "string") {
          prompt = json.prompt
          callback({ type: "result", stopReason: "end_turn" })
        }
      } catch {
        // Ignore non-JSON keepalive or partial lines.
      }
    }, () => {})

    if (prompt === null) throw new Error("No system prompt returned by the agent runtime.")
    return prompt
  }
}
